<template>
    <div class="wrapper">
        <div v-if="customer.customer_id && customer.staff">
            <div class="container marginLargeTop marginLargeBottom">
                <div class="help" @click="toggleHelp">Help<i class="icon">ℹ️</i></div>
                <h2>Auction Console</h2>
                <AgentStatusBadge></AgentStatusBadge>
            </div>
            <!-- Console Controls -->
            <div class="container two-column marginNormalTop paddingSmallBottom">
                <!-- The auction details component -->
                <AuctionDetailsCard :activeAuction="activeAuction" :nextAuction="nextAuction" :getActiveAuction="getActiveAuction"></AuctionDetailsCard>
                <!-- the messaging component -->
                <AuctionConsoleMessagingCard></AuctionConsoleMessagingCard>
            </div>
            <hr />
            <!-- Log streams -->
            <div class="container two-column paddingSmallBottom">
                <VirtualAuctioneerConsole :activeAuction="activeAuction" :nextAuction="nextAuction"></VirtualAuctioneerConsole>
                <ThirdPartyConsole :activeAuction="activeAuction" :nextAuction="nextAuction"></ThirdPartyConsole>
            </div>

            <hr />
            <div class="container paddingLargeBottom">
                <a class="cursorPointer" @click="clearBanner">Clear Banner</a>
            </div>
            <AgentInfoDrawer></AgentInfoDrawer>
            <HelpDocDrawer path="docs/user/auction/run.md"></HelpDocDrawer>
        </div>

        <LoadingOverlayComponent v-if="showLoading" v-bind:title="loadingTitle"></LoadingOverlayComponent>

        <!-- Generic Alert -->
        <AlertComponent v-if="showAlert" v-bind:title="alertTitle"
            v-bind:message="alertMessage" v-bind:secondaryAction=null @close="closeAlert()">
        </AlertComponent>
    </div>
</template>

<script>

import AgentStatusBadge from './sections/AgentStatusBadge.vue'
import AgentInfoDrawer from './sections/AgentInfoDrawer.vue'
import LoadingOverlayComponent from '@/components/Shared/LoadingOverlayComponent.vue'
import AlertComponent from '@/components/Shared/AlertComponent.vue'
import apiMixin from '@/mixins/apiMixin.js'
import VirtualAuctioneerLogStream from './sections/VirtualAuctioneerLogStream.vue'
import ThirdPartyLogStream from './sections/ThirdPartyLogStream.vue'
import AuctionConsoleMessagingCard from './sections/AuctionConsoleMessagingCard.vue'
import AuctionDetailsCard from './sections/AuctionDetailsCard.vue'
import VirtualAuctioneerConsole from './sections/VirtualAuctioneerConsole.vue'
import ThirdPartyConsole from './sections/ThirdPartyConsole.vue'
import HelpDocDrawer from './sections/HelpDocDrawer.vue'

export default {
    name: 'AuctionConsoleComponent',
    components: {
        AgentInfoDrawer,
        AgentStatusBadge,
        AuctionDetailsCard,
        AuctionConsoleMessagingCard,
        LoadingOverlayComponent,
        AlertComponent,
        HelpDocDrawer,
        ThirdPartyConsole,
        ThirdPartyLogStream,
        VirtualAuctioneerConsole,
        VirtualAuctioneerLogStream,
    },
    mixins: [apiMixin],
    data() {
        return {
            // Loading
            loadingTitle: 'Getting active auction details',
            loadingSubTitle: '',
            showLoading: true,
            // Alerts
            showAlert: false,
            alertTitle: '',
            alertMessage: '',
            // Current Auction Details
            activeAuction: null,
            // Next Auction Details
            nextAuction: null,
            virtualAuctioneerInfo: null,
        }
    },
    computed: {
        customer: function () {
            return this.$store.getters.customer
        },
        virtualAuctioneerScriptCrashed: function () {
            return this.$store.getters.virtualAuctioneerHasCrashed
        }
    },
    watch: {
        customer: function (customer) {
            // If not staff, cannot be here.
            if (customer.customer_id && !customer.staff) {
                this.exit()
            }
        },
        virtualAuctioneerScriptCrashed: function (crashed) {
            if (crashed) {
                // Restart the Virtual Auctioneer
                this.auctionRunning = false
                this.alertTitle = 'Error'
                this.alertMessage = 'Virtual Auctioneer script has crashed! Auction is being paused.'
                this.showAlert = true
                this.shouldCloseModalAndNotExit = true // when the alert is closed, don't exit the page
            }

            // Reset the flag
            this.$store.dispatch('processVirtualAuctioneerCrash', false)
        }
    },
    mounted() {
        this.getActiveAuction();
        // We should listen to the virtual-auctioneer-info-updated event and update the auction status
        this.$root.$on('virtual-auctioneer-info-updated', info => {
            if (info.active !== this.virtualAuctioneerInfo?.active) {
                this.getActiveAuction();
            }
            this.virtualAuctioneerInfo = info
        });
    },
    methods: {

        /**
         * Get the active auction details
         */
        getActiveAuction() {
            var cmp = this
            let apiPayload = {}
            apiPayload.apiEndpoint = 'active_auction'
            apiPayload.postData = {}
            apiPayload.postData.action = 'auction:console'

            cmp.$store.dispatch('apiCall', apiPayload).then(response => {
                this.showLoading = false
                if (response.data.result !== 'success') {
                    this.alertTitle = 'Error'
                    this.alertMessage = response.data.message
                    this.shouldCloseModalAndNotExit = true // when the alert is closed, don't exit the page
                    this.showAlert = true
                } else {
                    this.activeAuction = response.data.payload?.active_auction;
                    this.nextAuction = response.data.payload?.next_auction;
                }
            })
        },

        /**
         * Clear the banner
         */
        clearBanner(){
            let apiPayload = {}
            apiPayload.apiEndpoint = 'active_auction'
            apiPayload.postData = {}
            apiPayload.postData.action = 'clear_banner'

            this.loadingTitle = 'Clearing banner'
            this.$store.dispatch('apiCall', apiPayload).then(response => {
                this.showLoading = false
                if (response.data.result !== 'success') {
                    this.alertTitle = 'Error'
                    this.alertMessage = response.data.message
                    this.shouldCloseModalAndNotExit = true
                    this.showAlert = true
                } else{
                    this.alertTitle = 'Success'
                    this.alertMessage = 'Banner cleared'
                    this.shouldCloseModalAndNotExit = true
                    this.showAlert = true
                }
            })
        },

        /**
         * Close the alert
         */
        closeAlert() {
            this.showAlert = false
            if (this.shouldCloseModalAndNotExit === false) {
                this.exit()
            }
        },

        /**
         * Exit the page
         */
        exit() {
            window.location.replace(this.getUrlHost(this.$store.getters.apiPath))
        },

        /**
         * Toggle the help drawer
         */
        toggleHelp() {
            this.$root.$emit('toggle-help', 'user/auction/run.md');
        }
    }

}
</script>

<style scoped>
.container {
    margin: 0 auto;
    max-width: 1440px;
    display: flex;
    justify-content: center;
    gap: 1.5em;
}

.container.two-column>div {
    width: 50%;
}

button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.wrapper {
    position: relative;
}

.help {
    width: auto !important;
    position:absolute;
    cursor: pointer;
    left: 0;
}
.help .icon {
    margin-left: 5px;
}
</style>